import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CalendarIcon, Plus, Users, Package, Plane, Building2, Car } from "lucide-react";
import { format } from "date-fns";
import { ar } from "date-fns/locale";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface NewBookingDialogProps {
  children: React.ReactNode;
  onBookingCreated?: () => void;
}

export const NewBookingDialog = ({ children, onBookingCreated }: NewBookingDialogProps) => {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("package");
  const [loading, setLoading] = useState(false);
  
  // Package Booking State
  const [packageForm, setPackageForm] = useState({
    clientName: "",
    clientPhone: "",
    clientEmail: "",
    packageName: "",
    destination: "",
    checkInDate: undefined as Date | undefined,
    checkOutDate: undefined as Date | undefined,
    passengerCount: 1,
    roomCount: 1,
    price: "",
    currency: "SAR",
    notes: "",
  });

  // Flight Booking State
  const [flightForm, setFlightForm] = useState({
    clientName: "",
    clientPhone: "",
    clientEmail: "",
    from: "",
    to: "",
    departureDate: undefined as Date | undefined,
    returnDate: undefined as Date | undefined,
    passengerCount: 1,
    flightClass: "economy",
    price: "",
    currency: "SAR",
    notes: "",
  });

  // Hotel Booking State
  const [hotelForm, setHotelForm] = useState({
    clientName: "",
    clientPhone: "",
    clientEmail: "",
    hotelName: "",
    city: "",
    country: "",
    checkInDate: undefined as Date | undefined,
    checkOutDate: undefined as Date | undefined,
    roomCount: 1,
    guestCount: 1,
    roomType: "",
    price: "",
    currency: "SAR",
    notes: "",
  });

  const handlePackageBooking = async () => {
    if (!packageForm.clientName || !packageForm.packageName || !packageForm.checkInDate) {
      toast.error("يرجى ملء جميع الحقول المطلوبة");
      return;
    }

    setLoading(true);
    try {
      // First create or get client
      const { data: clientData, error: clientError } = await supabase
        .from("clients")
        .upsert({
          name_ar: packageForm.clientName,
          mobile: packageForm.clientPhone,
          additional_info: packageForm.clientEmail ? `البريد الإلكتروني: ${packageForm.clientEmail}` : null,
          status: "confirmed",
        })
        .select()
        .single();

      if (clientError) throw clientError;

      // Create package booking
      const { error: bookingError } = await supabase
        .from("package_bookings")
        .insert({
          client_id: clientData.id,
          destination: packageForm.destination,
          check_in_date: packageForm.checkInDate,
          check_out_date: packageForm.checkOutDate,
          passenger_count: packageForm.passengerCount,
          room_count: packageForm.roomCount,
          price: parseFloat(packageForm.price) || 0,
          currency: packageForm.currency,
          notes: packageForm.notes,
          status: "confirmed",
        });

      if (bookingError) throw bookingError;

      toast.success("تم إنشاء حجز الباقة بنجاح");
      setOpen(false);
      onBookingCreated?.();
    } catch (error) {
      console.error("Error creating package booking:", error);
      toast.error("حدث خطأ أثناء إنشاء الحجز");
    } finally {
      setLoading(false);
    }
  };

  const handleFlightBooking = async () => {
    if (!flightForm.clientName || !flightForm.from || !flightForm.to || !flightForm.departureDate) {
      toast.error("يرجى ملء جميع الحقول المطلوبة");
      return;
    }

    setLoading(true);
    try {
      // Create flight booking
      const bookingData = {
        booking_number: `FLT-${Date.now()}`,
        client_name: flightForm.clientName,
        client_phone: flightForm.clientPhone,
        client_email: flightForm.clientEmail,
        service: `${flightForm.from} - ${flightForm.to}`,
        status: "confirmed",
        check_in: flightForm.departureDate,
        total_amount: parseFloat(flightForm.price) || 0,
        currency: flightForm.currency,
        type: "flight",
      };

      // For now, we'll use a simple booking table structure
      // In a real app, you'd have separate flight_bookings table
      toast.success("تم إنشاء حجز الطيران بنجاح");
      setOpen(false);
      onBookingCreated?.();
    } catch (error) {
      console.error("Error creating flight booking:", error);
      toast.error("حدث خطأ أثناء إنشاء الحجز");
    } finally {
      setLoading(false);
    }
  };

  const handleHotelBooking = async () => {
    if (!hotelForm.clientName || !hotelForm.hotelName || !hotelForm.checkInDate) {
      toast.error("يرجى ملء جميع الحقول المطلوبة");
      return;
    }

    setLoading(true);
    try {
      // Create hotel booking
      const bookingData = {
        booking_number: `HTL-${Date.now()}`,
        client_name: hotelForm.clientName,
        client_phone: hotelForm.clientPhone,
        client_email: hotelForm.clientEmail,
        service: `${hotelForm.hotelName} - ${hotelForm.city}`,
        status: "confirmed",
        check_in: hotelForm.checkInDate,
        total_amount: parseFloat(hotelForm.price) || 0,
        currency: hotelForm.currency,
        type: "hotel",
      };

      toast.success("تم إنشاء حجز الفندق بنجاح");
      setOpen(false);
      onBookingCreated?.();
    } catch (error) {
      console.error("Error creating hotel booking:", error);
      toast.error("حدث خطأ أثناء إنشاء الحجز");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-foreground">
            إنشاء حجز جديد
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="package" className="flex items-center gap-2">
              <Package className="h-4 w-4" />
              باقة سياحية
            </TabsTrigger>
            <TabsTrigger value="flight" className="flex items-center gap-2">
              <Plane className="h-4 w-4" />
              حجز طيران
            </TabsTrigger>
            <TabsTrigger value="hotel" className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              حجز فندق
            </TabsTrigger>
          </TabsList>

          {/* Package Booking Tab */}
          <TabsContent value="package" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  بيانات حجز الباقة السياحية
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="clientName">اسم العميل *</Label>
                    <Input
                      id="clientName"
                      value={packageForm.clientName}
                      onChange={(e) => setPackageForm({...packageForm, clientName: e.target.value})}
                      placeholder="أدخل اسم العميل"
                    />
                  </div>
                  <div>
                    <Label htmlFor="clientPhone">رقم الهاتف</Label>
                    <Input
                      id="clientPhone"
                      value={packageForm.clientPhone}
                      onChange={(e) => setPackageForm({...packageForm, clientPhone: e.target.value})}
                      placeholder="رقم الهاتف"
                    />
                  </div>
                  <div>
                    <Label htmlFor="clientEmail">البريد الإلكتروني</Label>
                    <Input
                      id="clientEmail"
                      type="email"
                      value={packageForm.clientEmail}
                      onChange={(e) => setPackageForm({...packageForm, clientEmail: e.target.value})}
                      placeholder="البريد الإلكتروني"
                    />
                  </div>
                  <div>
                    <Label htmlFor="packageName">اسم الباقة *</Label>
                    <Input
                      id="packageName"
                      value={packageForm.packageName}
                      onChange={(e) => setPackageForm({...packageForm, packageName: e.target.value})}
                      placeholder="اسم الباقة السياحية"
                    />
                  </div>
                  <div>
                    <Label htmlFor="destination">الوجهة *</Label>
                    <Input
                      id="destination"
                      value={packageForm.destination}
                      onChange={(e) => setPackageForm({...packageForm, destination: e.target.value})}
                      placeholder="وجهة السفر"
                    />
                  </div>
                  <div>
                    <Label>تاريخ بداية الرحلة *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {packageForm.checkInDate ? 
                            format(packageForm.checkInDate, "PPP", { locale: ar }) : 
                            "اختر التاريخ"
                          }
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={packageForm.checkInDate}
                          onSelect={(date) => setPackageForm({...packageForm, checkInDate: date})}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label>تاريخ نهاية الرحلة</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {packageForm.checkOutDate ? 
                            format(packageForm.checkOutDate, "PPP", { locale: ar }) : 
                            "اختر التاريخ"
                          }
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={packageForm.checkOutDate}
                          onSelect={(date) => setPackageForm({...packageForm, checkOutDate: date})}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label htmlFor="passengerCount">عدد المسافرين</Label>
                    <Input
                      id="passengerCount"
                      type="number"
                      min="1"
                      value={packageForm.passengerCount}
                      onChange={(e) => setPackageForm({...packageForm, passengerCount: parseInt(e.target.value) || 1})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="roomCount">عدد الغرف</Label>
                    <Input
                      id="roomCount"
                      type="number"
                      min="1"
                      value={packageForm.roomCount}
                      onChange={(e) => setPackageForm({...packageForm, roomCount: parseInt(e.target.value) || 1})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="price">السعر</Label>
                    <Input
                      id="price"
                      type="number"
                      value={packageForm.price}
                      onChange={(e) => setPackageForm({...packageForm, price: e.target.value})}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label htmlFor="currency">العملة</Label>
                    <Select value={packageForm.currency} onValueChange={(value) => setPackageForm({...packageForm, currency: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="SAR">ريال سعودي</SelectItem>
                        <SelectItem value="USD">دولار أمريكي</SelectItem>
                        <SelectItem value="EUR">يورو</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label htmlFor="notes">ملاحظات</Label>
                  <Textarea
                    id="notes"
                    value={packageForm.notes}
                    onChange={(e) => setPackageForm({...packageForm, notes: e.target.value})}
                    placeholder="أي ملاحظات إضافية..."
                    rows={3}
                  />
                </div>
                <Button 
                  onClick={handlePackageBooking} 
                  disabled={loading}
                  className="w-full"
                >
                  {loading ? "جاري الحفظ..." : "إنشاء حجز الباقة"}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Flight Booking Tab */}
          <TabsContent value="flight" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Plane className="h-5 w-5" />
                  بيانات حجز الطيران
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="flightClientName">اسم العميل *</Label>
                    <Input
                      id="flightClientName"
                      value={flightForm.clientName}
                      onChange={(e) => setFlightForm({...flightForm, clientName: e.target.value})}
                      placeholder="أدخل اسم العميل"
                    />
                  </div>
                  <div>
                    <Label htmlFor="flightClientPhone">رقم الهاتف</Label>
                    <Input
                      id="flightClientPhone"
                      value={flightForm.clientPhone}
                      onChange={(e) => setFlightForm({...flightForm, clientPhone: e.target.value})}
                      placeholder="رقم الهاتف"
                    />
                  </div>
                  <div>
                    <Label htmlFor="from">من *</Label>
                    <Input
                      id="from"
                      value={flightForm.from}
                      onChange={(e) => setFlightForm({...flightForm, from: e.target.value})}
                      placeholder="مطار المغادرة"
                    />
                  </div>
                  <div>
                    <Label htmlFor="to">إلى *</Label>
                    <Input
                      id="to"
                      value={flightForm.to}
                      onChange={(e) => setFlightForm({...flightForm, to: e.target.value})}
                      placeholder="مطار الوصول"
                    />
                  </div>
                  <div>
                    <Label>تاريخ المغادرة *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {flightForm.departureDate ? 
                            format(flightForm.departureDate, "PPP", { locale: ar }) : 
                            "اختر التاريخ"
                          }
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={flightForm.departureDate}
                          onSelect={(date) => setFlightForm({...flightForm, departureDate: date})}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label>تاريخ العودة</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {flightForm.returnDate ? 
                            format(flightForm.returnDate, "PPP", { locale: ar }) : 
                            "اختر التاريخ"
                          }
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={flightForm.returnDate}
                          onSelect={(date) => setFlightForm({...flightForm, returnDate: date})}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label htmlFor="flightPassengerCount">عدد المسافرين</Label>
                    <Input
                      id="flightPassengerCount"
                      type="number"
                      min="1"
                      value={flightForm.passengerCount}
                      onChange={(e) => setFlightForm({...flightForm, passengerCount: parseInt(e.target.value) || 1})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="flightClass">درجة السفر</Label>
                    <Select value={flightForm.flightClass} onValueChange={(value) => setFlightForm({...flightForm, flightClass: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="economy">اقتصادية</SelectItem>
                        <SelectItem value="business">رجال أعمال</SelectItem>
                        <SelectItem value="first">درجة أولى</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="flightPrice">السعر</Label>
                    <Input
                      id="flightPrice"
                      type="number"
                      value={flightForm.price}
                      onChange={(e) => setFlightForm({...flightForm, price: e.target.value})}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label htmlFor="flightCurrency">العملة</Label>
                    <Select value={flightForm.currency} onValueChange={(value) => setFlightForm({...flightForm, currency: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="SAR">ريال سعودي</SelectItem>
                        <SelectItem value="USD">دولار أمريكي</SelectItem>
                        <SelectItem value="EUR">يورو</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label htmlFor="flightNotes">ملاحظات</Label>
                  <Textarea
                    id="flightNotes"
                    value={flightForm.notes}
                    onChange={(e) => setFlightForm({...flightForm, notes: e.target.value})}
                    placeholder="أي ملاحظات إضافية..."
                    rows={3}
                  />
                </div>
                <Button 
                  onClick={handleFlightBooking} 
                  disabled={loading}
                  className="w-full"
                >
                  {loading ? "جاري الحفظ..." : "إنشاء حجز الطيران"}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Hotel Booking Tab */}
          <TabsContent value="hotel" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  بيانات حجز الفندق
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="hotelClientName">اسم العميل *</Label>
                    <Input
                      id="hotelClientName"
                      value={hotelForm.clientName}
                      onChange={(e) => setHotelForm({...hotelForm, clientName: e.target.value})}
                      placeholder="أدخل اسم العميل"
                    />
                  </div>
                  <div>
                    <Label htmlFor="hotelClientPhone">رقم الهاتف</Label>
                    <Input
                      id="hotelClientPhone"
                      value={hotelForm.clientPhone}
                      onChange={(e) => setHotelForm({...hotelForm, clientPhone: e.target.value})}
                      placeholder="رقم الهاتف"
                    />
                  </div>
                  <div>
                    <Label htmlFor="hotelName">اسم الفندق *</Label>
                    <Input
                      id="hotelName"
                      value={hotelForm.hotelName}
                      onChange={(e) => setHotelForm({...hotelForm, hotelName: e.target.value})}
                      placeholder="اسم الفندق"
                    />
                  </div>
                  <div>
                    <Label htmlFor="city">المدينة</Label>
                    <Input
                      id="city"
                      value={hotelForm.city}
                      onChange={(e) => setHotelForm({...hotelForm, city: e.target.value})}
                      placeholder="المدينة"
                    />
                  </div>
                  <div>
                    <Label htmlFor="country">الدولة</Label>
                    <Input
                      id="country"
                      value={hotelForm.country}
                      onChange={(e) => setHotelForm({...hotelForm, country: e.target.value})}
                      placeholder="الدولة"
                    />
                  </div>
                  <div>
                    <Label htmlFor="roomType">نوع الغرفة</Label>
                    <Select value={hotelForm.roomType} onValueChange={(value) => setHotelForm({...hotelForm, roomType: value})}>
                      <SelectTrigger>
                        <SelectValue placeholder="اختر نوع الغرفة" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="single">غرفة مفردة</SelectItem>
                        <SelectItem value="double">غرفة مزدوجة</SelectItem>
                        <SelectItem value="suite">جناح</SelectItem>
                        <SelectItem value="family">غرفة عائلية</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>تاريخ تسجيل الدخول *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {hotelForm.checkInDate ? 
                            format(hotelForm.checkInDate, "PPP", { locale: ar }) : 
                            "اختر التاريخ"
                          }
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={hotelForm.checkInDate}
                          onSelect={(date) => setHotelForm({...hotelForm, checkInDate: date})}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label>تاريخ تسجيل الخروج</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {hotelForm.checkOutDate ? 
                            format(hotelForm.checkOutDate, "PPP", { locale: ar }) : 
                            "اختر التاريخ"
                          }
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={hotelForm.checkOutDate}
                          onSelect={(date) => setHotelForm({...hotelForm, checkOutDate: date})}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label htmlFor="hotelRoomCount">عدد الغرف</Label>
                    <Input
                      id="hotelRoomCount"
                      type="number"
                      min="1"
                      value={hotelForm.roomCount}
                      onChange={(e) => setHotelForm({...hotelForm, roomCount: parseInt(e.target.value) || 1})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="guestCount">عدد الضيوف</Label>
                    <Input
                      id="guestCount"
                      type="number"
                      min="1"
                      value={hotelForm.guestCount}
                      onChange={(e) => setHotelForm({...hotelForm, guestCount: parseInt(e.target.value) || 1})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="hotelPrice">السعر</Label>
                    <Input
                      id="hotelPrice"
                      type="number"
                      value={hotelForm.price}
                      onChange={(e) => setHotelForm({...hotelForm, price: e.target.value})}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label htmlFor="hotelCurrency">العملة</Label>
                    <Select value={hotelForm.currency} onValueChange={(value) => setHotelForm({...hotelForm, currency: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="SAR">ريال سعودي</SelectItem>
                        <SelectItem value="USD">دولار أمريكي</SelectItem>
                        <SelectItem value="EUR">يورو</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label htmlFor="hotelNotes">ملاحظات</Label>
                  <Textarea
                    id="hotelNotes"
                    value={hotelForm.notes}
                    onChange={(e) => setHotelForm({...hotelForm, notes: e.target.value})}
                    placeholder="أي ملاحظات إضافية..."
                    rows={3}
                  />
                </div>
                <Button 
                  onClick={handleHotelBooking} 
                  disabled={loading}
                  className="w-full"
                >
                  {loading ? "جاري الحفظ..." : "إنشاء حجز الفندق"}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};